import { im_proto } from '../proto/index.js';
import { Version } from '../version.js';

/** @internal */
const SdkConsts = {
    sdkVersion: Version.version,
    refer: im_proto.Refer.PC,
    buildNumber: `${Version.commit}:${Version.branch}`,
    wsProtocols: ['binary', 'base64', 'pbbp2'],
    ticketType: im_proto.TicketType.TICKET_TYPE_WEB,
    envKey: 'X-Tt-Env',
    boeHeaderKey: 'X-Use-Boe',
    ppeHeaderKey: 'X-Use-Ppe',
    requestAccessName: 'web_sdk',
    heartbeatInterval: 15000,
    maxHeartbeatEmptyWindow: 30000,
    conversationRefreshCount: 10
};
/** WebSocket 启用级别 */
var WebSocketLevel;
(function (WebSocketLevel) {
    /** 使用 SDK 默认配置, 大部分接口通过 WebSocket 发送请求, 少数通过 HTTP */
    WebSocketLevel[WebSocketLevel["Default"] = 0] = "Default";
    /** 只通过 WebSocket 接收 Push 消息, 所有请求都使用 HTTP 发送 */
    WebSocketLevel[WebSocketLevel["PushOnly"] = 1] = "PushOnly";
    /** 所有接口均使用 WebSocket */
    WebSocketLevel[WebSocketLevel["All"] = 2] = "All";
    /** 完全不连接 WebSocket */
    WebSocketLevel[WebSocketLevel["Disable"] = 3] = "Disable";
})(WebSocketLevel || (WebSocketLevel = {}));
/** Ext 字段 Key */
var InfoKeys;
(function (InfoKeys) {
    InfoKeys["MessageMode"] = "s:mode";
    /** 发送消息 Response 中的 statusCode 字段 */
    InfoKeys["SendResponseStatus"] = "s:send_response_status";
    /** 发送消息 Response 中的 extraInfo 字段 */
    InfoKeys["SendResponseExtraInfo"] = "s:send_response_extra_info";
    /** 发送消息 Response 中的 checkCode 字段 */
    InfoKeys["SendResponseCheckCode"] = "s:send_response_check_code";
    /** 发送消息 Response 中的 checkMessage 字段 */
    InfoKeys["SendResponseCheckMessage"] = "s:send_response_check_msg";
    /**
     * 消息客户端 Id
     * {@link Message.clientId}
     */
    InfoKeys["ClientMessageId"] = "s:client_message_id";
    /**
     * 被 @ 的用户
     * {@link Message.mentionedUser}
     */
    InfoKeys["MentionedUser"] = "s:mentioned_users";
    /**
     * 设置这个标记为 `'true'` 时, 客户端不会把这条消息算入未读数内
     * 可以用于退群系统消息等
     */
    InfoKeys["DoNotIncreaseUnread"] = "s:do_not_increase_unread";
    /**
     * 设置这个标记为 `'true'` 时, 客户端不会因为这条消息而把会话往上顶
     * 可以用于退群系统消息等
     */
    InfoKeys["DoNotPopConversation"] = "s:do_not_pop_conversation";
    /** 消息被撤回 */
    InfoKeys["IsRecalled"] = "s:is_recalled";
    /** 消息的 Server Id */
    InfoKeys["ServerMessageId"] = "s:server_message_id";
    /** logid, 上报 RTT 用 */
    InfoKeys["LocalLogId"] = "s:local_logid";
    /** 消息可见用户 */
    InfoKeys["MessageVisible"] = "s:visible";
    /** 消息不可见用户 */
    InfoKeys["MessageInvisible"] = "s:invisible";
    /** 会话是否开启禁言 **/
    InfoKeys["RelationIsMuted"] = "s:relation_is_muted";
    /** 是否只禁言普通成员 **/
    InfoKeys["RelationNormalOnly"] = "s:relation_normal_only";
    InfoKeys["RelationMuteTime"] = "s:relation_mute_time";
    InfoKeys["RelationMuteExt"] = "s:relation_mute_ext";
    InfoKeys["MessageSourceAppId"] = "s:biz_aid";
    InfoKeys["ConversationSourceAppId"] = "s:s_aid";
    InfoKeys["MarkActionType"] = "s:action_type";
    InfoKeys["IsRootReference"] = "s:is_root_ref";
    InfoKeys["MarkMessageNewExt"] = "s:mark_message_new_ext";
    InfoKeys["AckSampling"] = "s:is_ack_sampling";
    InfoKeys["AckSamplingShow"] = "s:is_ack_sampling_show";
    InfoKeys["DoNotUpdateLastMessage"] = "s:do_not_update_last_msg";
    InfoKeys["DoNotMoveReadIndex"] = "s:do_not_move_read_index";
    /** 音频消息转换后的文字 */
    InfoKeys["FileExtKeyAudioAsrText"] = "s:file_ext_key_audio_asr_text";
    /** 保存check_code 透传给用户 约定命中敏感词 用户在tcc上配置*/
    InfoKeys["RecognitionResponseCheckCode"] = "s:recognition_response_check_code";
    /** 保存check_message 透传给用户 */
    InfoKeys["RecognitionResponseCheckMsg"] = "s:recognition_response_check_msg";
    /**
     * 低打扰模式时，设置关注的用户/消息类型
     */
    InfoKeys["PushPartDisableConfig"] = "s:push_part_disable_config";
    /**
     * 消息强制通知
     */
    InfoKeys["MustNotify"] = "s:must_notify";
})(InfoKeys || (InfoKeys = {}));
const PushPartDisableConfigWhiteListKey = 'mute_wl';
/** 消息服务端状态 */
var ServerMessageStatus;
(function (ServerMessageStatus) {
    /** 启用 (正常状态) */
    ServerMessageStatus[ServerMessageStatus["Enable"] = 0] = "Enable";
    /** 禁用 (不显示) */
    ServerMessageStatus[ServerMessageStatus["Disable"] = 1] = "Disable";
})(ServerMessageStatus || (ServerMessageStatus = {}));
/** 会话类型 */
var ConversationType;
(function (ConversationType) {
    /** 单聊会话 */
    ConversationType[ConversationType["SingleChat"] = 1] = "SingleChat";
    /** 群聊会话 */
    ConversationType[ConversationType["GroupChat"] = 2] = "GroupChat";
    /** 轻直播会话 */
    ConversationType[ConversationType["LiveChat"] = 3] = "LiveChat";
})(ConversationType || (ConversationType = {}));
/** 会话状态 */
var ConversationStatus;
(function (ConversationStatus) {
    /** 正常状态 */
    ConversationStatus[ConversationStatus["Normal"] = 0] = "Normal";
    /** 已被解散 */
    ConversationStatus[ConversationStatus["Dissolved"] = 1] = "Dissolved";
})(ConversationStatus || (ConversationStatus = {}));
/** 置顶状态 */
var StickTopState;
(function (StickTopState) {
    /** 未置顶 */
    StickTopState[StickTopState["Off"] = 0] = "Off";
    /** 已置顶 */
    StickTopState[StickTopState["On"] = 1] = "On";
})(StickTopState || (StickTopState = {}));
/** 免打扰状态 */
var MuteState;
(function (MuteState) {
    /** 普通提醒 */
    MuteState[MuteState["Off"] = 0] = "Off";
    /** 免打扰 (不提醒) */
    MuteState[MuteState["On"] = 1] = "On";
})(MuteState || (MuteState = {}));
/** 消息推送模式 */
var PushStatus;
(function (PushStatus) {
    /**
     * 服务端未返回，或不支持的类型
     */
    PushStatus[PushStatus["Unknown"] = 0] = "Unknown";
    /**
     * 正常模式（允许推送）
     */
    PushStatus[PushStatus["Allow"] = 1] = "Allow";
    /**
     * 免打扰模式（禁止推送）
     */
    PushStatus[PushStatus["Disable"] = 2] = "Disable";
    /**
     * 低打扰模式（部分推送）
     */
    PushStatus[PushStatus["PartAllow"] = 3] = "PartAllow";
})(PushStatus || (PushStatus = {}));
/** 收藏状态 */
var FavoriteState;
(function (FavoriteState) {
    /** 未收藏 */
    FavoriteState[FavoriteState["Off"] = 0] = "Off";
    /** 已收藏 */
    FavoriteState[FavoriteState["On"] = 1] = "On";
})(FavoriteState || (FavoriteState = {}));
/** SDK 初始化状态 */
var InitResult;
(function (InitResult) {
    /** 不可用 / 未知 */
    InitResult[InitResult["NotAvailable"] = 0] = "NotAvailable";
    /** 正在初始化 */
    InitResult[InitResult["Start"] = 1] = "Start";
    /** 初始化出错 */
    InitResult[InitResult["Error"] = 2] = "Error";
    /** 初始化成功完成 */
    InitResult[InitResult["Succeeded"] = 3] = "Succeeded";
})(InitResult || (InitResult = {}));
/**
 * 消息来源
 * {@link InfoKeys.MessageSource}
 */
var MessageSource;
(function (MessageSource) {
    MessageSource[MessageSource["Unknown"] = 0] = "Unknown";
    /** 长链 push */
    MessageSource[MessageSource["Online"] = 1] = "Online";
    /** 手动 load more 拉单链 */
    MessageSource[MessageSource["LoadMore"] = 2] = "LoadMore";
    /** 初始化 */
    MessageSource[MessageSource["Init"] = 3] = "Init";
    /** 混链补偿 */
    MessageSource[MessageSource["UserInbox"] = 4] = "UserInbox";
    /** 超大群拉单链 */
    MessageSource[MessageSource["BroadcastLoadMore"] = 5] = "BroadcastLoadMore";
    /** 本地创建的消息 (自己发送或 insertOfflineMessage 创建的消息) */
    MessageSource[MessageSource["Offline"] = 6] = "Offline";
})(MessageSource || (MessageSource = {}));
/** IM 事件 */
var IMEvent;
(function (IMEvent) {
    /**
     * 内部抛出异常时触发
     * @param BaseError
     */
    IMEvent["Error"] = "error";
    /**
     * 长链建联
     */
    IMEvent["WebSocketConnected"] = "websocket-connected";
    /**
     * 长链断开
     */
    IMEvent["WebSocketDisconnected"] = "websocket-disconnected";
    /**
     * WebSocket 收到了非预期 Service 的 Frame, 可能是业务侧的
     */
    IMEvent["WebSocketReceiveUnexpectedFrame"] = "websocket-receive-unexpected-frame";
    /**
     * 通过混链补偿或长链推送收到新消息时触发(非50000+新消息), 需要自己的消息也触发时启用 noticeSelfMessage
     * @param Message 新收到的消息
     */
    IMEvent["ReceiveNewMessage"] = "receive-new-message";
    /**
     * 收到自己发送的消息
     */
    IMEvent["ReceiveSelfMessage"] = "receive-self-message";
    /**
     * 收到命令消息
     */
    IMEvent["ReceiveCommandMessage"] = "receive-command-message";
    /**
     * 超大群收到服务端消息通知
     */
    IMEvent["ReceiveBroadcastNewMessage"] = "receive-broadcast-new-message";
    /**
     * 超大群收到自己发送的消息通知
     */
    IMEvent["ReceiveBroadcastSelfMessage"] = "receive-broadcast-self-message";
    /**
     * 收到 50002 消息时触发, 代表消息的 Ext 已经更改
     * 消息被撤回, 被删除等也会触发这个事件
     * @param Message 50002 消息
     */
    IMEvent["ReceiveNewUpdateExtMessage"] = "receive-new-update-ext-message";
    IMEvent["ReceiveNewP2PMessage"] = "receive-new-p2p-message";
    /**
     * 在消息更新时触发
     * @param Message 被更新的消息
     */
    IMEvent["MessageUpsert"] = "message-upsert";
    /**
     * 在会话, 或会话内的消息发生变更时触发
     */
    IMEvent["ConversationChange"] = "conversation-change";
    /**
     * 在会话更新时触发
     * @param Conversation 被更新的会话
     */
    IMEvent["ConversationUpsert"] = "conversation-upsert";
    /**
     * 在会话解散时触发
     * @param Conversation 被解散的会话
     */
    IMEvent["ConversationDissolve"] = "conversation-dissolve";
    /**
     * 50011 全群禁言
     */
    IMEvent["ConversationBlock"] = "conversation-block";
    // 来自cmd消息
    /** 群成员变更*/
    IMEvent["ParticipantUpsert"] = "participant-upsert";
    /** 成员进群*/
    IMEvent["ParticipantJoin"] = "participant-join";
    /**
     * 成员离开群聊，主动退群或者被提出群聊，包括自己
     * @returns Participant 离开群聊的成员
     */
    IMEvent["ParticipantLeave"] = "participant-leave";
    /**
     * 50011 会话内成员被禁言
     */
    IMEvent["ParticipantBlock"] = "participant-block";
    /**
     * 消息发送
     * @param Message 发送的消息
     */
    IMEvent["MessageSend"] = "message-send";
    /**
     * 消息被撤回
     * @param Message 被撤回的消息
     */
    IMEvent["MessageRecall"] = "message-recall";
    /**
     * 消息被删除
     * @param Message 被删除的消息
     */
    IMEvent["MessageDelete"] = "message-delete";
    // TODO: 很多都没实现
    /**
     * 删除会话
     * @param Conversation 被删除的会话
     */
    IMEvent["ConversationDelete"] = "conversation-delete";
    /**
     * 离开会话 自己退群或者被提出群聊
     * @param Conversation 离开的会话
     */
    IMEvent["ConversationLeave"] = "conversation-leave";
    /**
     * 创建会话
     */
    IMEvent["ConversationCreate"] = "conversation-create";
    /**
     * 加入会话 进群
     * @param Conversation 加入的会话
     */
    IMEvent["ConversationJoin"] = "conversation-join";
    // ClearMessage = 'clearMessage' // 50001:9 不用了
    // 下面这些以后再开放
    /*
    QueryConversation,
    LoadMember,
    UpdateMembers,
    AddMembers,
    RemoveMembers,
    */
    /**
     * 收到新陌生人消息
     * @param Message 收到的消息
     */
    IMEvent["ReceiveNewStrangerMessage"] = "receive-new-stranger-message";
    /**
     * 陌生人转正
     * @param Conversation 发生转正的会话
     */
    IMEvent["StrangerUpgrade"] = "stranger-upgrade";
    IMEvent["ReceiveRtc"] = "receive-rtc";
    /**
     * 消息属性变更
     * @param Message 变更的消息
     */
    IMEvent["MessagePropertyUpsert"] = "message-property-upsert";
    /**
     * 初始化拉取历史消息，分页
     */
    IMEvent["InitLoadPage"] = "init-load-page";
    /**
     * 初始化成功完成
     */
    IMEvent["InitFinish"] = "init-finish";
})(IMEvent || (IMEvent = {}));
/** IM 错误类型 */
var ErrorType;
(function (ErrorType) {
    /** 未知错误 */
    ErrorType[ErrorType["Unknown"] = -1] = "Unknown";
    /** 成功 */
    ErrorType[ErrorType["Success"] = 0] = "Success";
    /** 无效tokne */
    ErrorType[ErrorType["InvalidToken"] = 1] = "InvalidToken";
    /** 无效ticket */
    ErrorType[ErrorType["InvalidTicket"] = 2] = "InvalidTicket";
    /** 取消请求 */
    ErrorType[ErrorType["InvalidRequest"] = 4] = "InvalidRequest";
    /** 无效命令 */
    ErrorType[ErrorType["InvalidCommand"] = 5] = "InvalidCommand";
    /** 服务端错误 */
    ErrorType[ErrorType["ServerError"] = 6] = "ServerError";
    ErrorType[ErrorType["UserForbidden"] = 11] = "UserForbidden";
    ErrorType[ErrorType["MessageTargetConversationNotExist"] = 15] = "MessageTargetConversationNotExist";
    /** 服务降级 */
    ErrorType[ErrorType["Degradation"] = 16] = "Degradation";
    /** 撤回超时 */
    ErrorType[ErrorType["RecallTimeout"] = 17] = "RecallTimeout";
    ErrorType[ErrorType["CallbackDeny"] = 19] = "CallbackDeny";
    /** token过期 */
    ErrorType[ErrorType["ExpiredToken"] = 100] = "ExpiredToken";
    /** 传入参数不合法 */
    ErrorType[ErrorType["InvalidParam"] = 400] = "InvalidParam";
    /** 资源耗尽 */
    ErrorType[ErrorType["ResourceExhausted"] = 429] = "ResourceExhausted";
    /** 内部错误 */
    ErrorType[ErrorType["InternalError"] = 500] = "InternalError";
    /** 不合法的 InboxType, 由于某个必须指定 InboxType 的接口未传入 InboxType 导致 */
    ErrorType[ErrorType["InvalidInboxType"] = 1000] = "InvalidInboxType";
    /**
     * 会话不存在
     * {@link ConversationNotExistError}
     */
    ErrorType[ErrorType["ConversationNotExist"] = 1001] = "ConversationNotExist";
    /** 消息不存在 */
    ErrorType[ErrorType["MessageNotExist"] = 1002] = "MessageNotExist";
    /** 消息尚未发送（消息离线保存） */
    ErrorType[ErrorType["MessageOffline"] = 1003] = "MessageOffline";
    /** 无法识别的消息类型, 收到了 type 为负数的消息 */
    ErrorType[ErrorType["UnknownMessageType"] = 1004] = "UnknownMessageType";
    /** 不合法的 Message Server Id, 收到 50002 消息时没有 s:server_message_id 字段 */
    ErrorType[ErrorType["InvalidServerId"] = 1005] = "InvalidServerId";
    /** 消息尚未准备好, 在消息还未到达服务端时就尝试对其进行操作 */
    ErrorType[ErrorType["MessageNotReady"] = 1006] = "MessageNotReady";
    /** Token 函数失败, 自动刷新 Token 机制未能获取到可用的新 Token */
    ErrorType[ErrorType["TokenFuncError"] = 1007] = "TokenFuncError";
    /** 网络错误 */
    ErrorType[ErrorType["NetworkError"] = 1008] = "NetworkError";
    /** 已销毁 */
    ErrorType[ErrorType["AlreadyDispose"] = 1009] = "AlreadyDispose";
    /** 适配器不存在 */
    ErrorType[ErrorType["NoAdapter"] = 1010] = "NoAdapter";
    ErrorType[ErrorType["ComponentNotFound"] = 1011] = "ComponentNotFound";
    /** 未实现某功能 */
    ErrorType[ErrorType["NotImplemented"] = 1012] = "NotImplemented";
    // MultimediPlugin: 10000+
    /**
     * 需要 {@link MultimediaPlugin}
     *
     * 参数不合法
     */
    ErrorType[ErrorType["MPInvalidArgument"] = 10001] = "MPInvalidArgument";
    /**
     * 需要 {@link MultimediaPlugin}
     *
     * 无法解析返回的 url
     */
    ErrorType[ErrorType["MPServerUrlError"] = 10002] = "MPServerUrlError";
    /**
     * 需要 {@link MultimediaPlugin}
     *
     * 不是一个文件消息
     */
    ErrorType[ErrorType["MPNotFileMsg"] = 10003] = "MPNotFileMsg";
    /**
     * 需要 {@link MultimediaPlugin}
     *
     * 上传出错
     */
    ErrorType[ErrorType["MPUploadError"] = 10004] = "MPUploadError";
    /** 解密失败 */
    ErrorType[ErrorType["MPNotSupportCipher"] = 10005] = "MPNotSupportCipher";
    // RTC: 10100+
    ErrorType[ErrorType["RtcUnknown"] = 10100] = "RtcUnknown";
    ErrorType[ErrorType["RtcCreateRoomFail"] = 10101] = "RtcCreateRoomFail";
    ErrorType[ErrorType["RtcStartCallFail"] = 10102] = "RtcStartCallFail";
    ErrorType[ErrorType["RtcAlreadyCalling"] = 10103] = "RtcAlreadyCalling";
    ErrorType[ErrorType["RtcAlreadyOnTheCall"] = 10104] = "RtcAlreadyOnTheCall";
    ErrorType[ErrorType["RtcNotCalling"] = 10105] = "RtcNotCalling";
    ErrorType[ErrorType["RtcNotOnTheCall"] = 10106] = "RtcNotOnTheCall";
    ErrorType[ErrorType["RtcNotRinging"] = 10107] = "RtcNotRinging";
    ErrorType[ErrorType["RtcAcceptFail"] = 10108] = "RtcAcceptFail";
    ErrorType[ErrorType["RtcCheckStatusFail"] = 10109] = "RtcCheckStatusFail";
    ErrorType[ErrorType["RtcInvalidParam"] = 10110] = "RtcInvalidParam";
    ErrorType[ErrorType["RtcNotInit"] = 10111] = "RtcNotInit";
    ErrorType[ErrorType["RtcNoClient"] = 10112] = "RtcNoClient";
    ErrorType[ErrorType["RtcNoInfo"] = 10113] = "RtcNoInfo";
    ErrorType[ErrorType["RtcNoStream"] = 10114] = "RtcNoStream";
    ErrorType[ErrorType["RtcSdkError"] = 10115] = "RtcSdkError";
    ErrorType[ErrorType["RtcNotSupported"] = 10116] = "RtcNotSupported";
    ErrorType[ErrorType["DbOpError"] = 20000] = "DbOpError";
    ErrorType[ErrorType["StorageCryptoError"] = 20001] = "StorageCryptoError";
    ErrorType[ErrorType["AuthSignError"] = 10201] = "AuthSignError";
})(ErrorType || (ErrorType = {}));
/** 消息发送状态 */
var FlightStatus;
(function (FlightStatus) {
    /** 消息已经创建 */
    FlightStatus[FlightStatus["Created"] = 0] = "Created";
    /** 准备中 (图片正在上传等) */
    FlightStatus[FlightStatus["Preparing"] = 1] = "Preparing";
    /** 发送中 (已经提交请求, 尚未收到响应) */
    FlightStatus[FlightStatus["Inflight"] = 2] = "Inflight";
    /** 发送成功 (已经收到响应) */
    FlightStatus[FlightStatus["Succeeded"] = 3] = "Succeeded";
    FlightStatus[FlightStatus["Received"] = 4] = "Received";
    /** 发送失败 (网络错误, 内部错误等) */
    FlightStatus[FlightStatus["Failed"] = -1] = "Failed";
    /** 消息被服务端拒绝 (回调检查) */
    FlightStatus[FlightStatus["Rejected"] = -2] = "Rejected";
    /** 消息被自见 */
    FlightStatus[FlightStatus["SelfVisible"] = -3] = "SelfVisible";
})(FlightStatus || (FlightStatus = {}));
var SendMessageStatusCode;
(function (SendMessageStatusCode) {
    SendMessageStatusCode[SendMessageStatusCode["Succeeded"] = 0] = "Succeeded";
    SendMessageStatusCode[SendMessageStatusCode["UserNotInConversation"] = 1] = "UserNotInConversation";
    SendMessageStatusCode[SendMessageStatusCode["CheckConversationNotPass"] = 2] = "CheckConversationNotPass";
    SendMessageStatusCode[SendMessageStatusCode["CheckMessageNotPass"] = 3] = "CheckMessageNotPass";
    SendMessageStatusCode[SendMessageStatusCode["CheckMessageNotPassButSelfVisible"] = 4] = "CheckMessageNotPassButSelfVisible";
    SendMessageStatusCode[SendMessageStatusCode["UserHasBeenBlock"] = 5] = "UserHasBeenBlock";
})(SendMessageStatusCode || (SendMessageStatusCode = {}));
var CreateConvertsationStatusCode;
(function (CreateConvertsationStatusCode) {
    CreateConvertsationStatusCode[CreateConvertsationStatusCode["Succeeded"] = 0] = "Succeeded";
    CreateConvertsationStatusCode[CreateConvertsationStatusCode["Rejected"] = 1] = "Rejected";
    CreateConvertsationStatusCode[CreateConvertsationStatusCode["PartialRejected"] = 2] = "PartialRejected";
})(CreateConvertsationStatusCode || (CreateConvertsationStatusCode = {}));
var TriggerType;
(function (TriggerType) {
    TriggerType[TriggerType["Immediate"] = 0] = "Immediate";
    TriggerType[TriggerType["Throttle"] = 1] = "Throttle";
    TriggerType[TriggerType["Debounce"] = 2] = "Debounce";
    TriggerType[TriggerType["ThrottleWithArgs"] = 3] = "ThrottleWithArgs";
    TriggerType[TriggerType["DebounceWithArgs"] = 4] = "DebounceWithArgs";
})(TriggerType || (TriggerType = {}));
var InitApiMode;
(function (InitApiMode) {
    InitApiMode[InitApiMode["Default"] = 0] = "Default";
    InitApiMode[InitApiMode["AwemeMode"] = 1] = "AwemeMode";
})(InitApiMode || (InitApiMode = {}));

export { ConversationStatus, ConversationType, CreateConvertsationStatusCode, ErrorType, FavoriteState, FlightStatus, IMEvent, InfoKeys, InitApiMode, InitResult, MessageSource, MuteState, PushPartDisableConfigWhiteListKey, PushStatus, SdkConsts, SendMessageStatusCode, ServerMessageStatus, StickTopState, TriggerType, WebSocketLevel };
//# sourceMappingURL=consts.js.map
